import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const NotificationContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastCheckRef = useRef({});
  const pollingRef = useRef(null);
  const currentUserIdRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Get auth headers from localStorage
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!localStorage.getItem('token');
  }, []);

  // Check for new messages in conversations
  const checkForNewMessages = useCallback(async () => {
    if (!isAuthenticated() || !isInitializedRef.current) return;

    try {
      const headers = getAuthHeaders();
      
      // Get current user ID if not set
      if (!currentUserIdRef.current) {
        const meResponse = await axios.get(`${API}/auth/me`, { headers });
        currentUserIdRef.current = meResponse.data.id;
      }

      const response = await axios.get(`${API}/conversations`, { headers });
      const conversations = response.data;

      for (const conv of conversations) {
        const messagesRes = await axios.get(`${API}/conversations/${conv.id}/messages`, { headers });
        const messages = messagesRes.data;
        
        if (messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          const lastChecked = lastCheckRef.current[conv.id];
          
          // If this is a new message from the other party (not system, not current user)
          const isFromOther = lastMessage.senderId !== currentUserIdRef.current && !lastMessage.isSystem;
          
          if (lastChecked && lastMessage.sentAt > lastChecked && isFromOther) {
            // Show toast notification
            toast.info(`New message from ${lastMessage.senderName}`, {
              description: lastMessage.body.substring(0, 60) + (lastMessage.body.length > 60 ? '...' : ''),
              action: {
                label: 'Open Chat',
                onClick: () => {
                  window.location.href = `/conversations/${conv.id}`;
                }
              },
              duration: 10000
            });
            
            // Add notification to state
            const newNotification = {
              id: `${conv.id}-${lastMessage.id}`,
              conversationId: conv.id,
              message: `${lastMessage.senderName}: ${lastMessage.body.substring(0, 50)}${lastMessage.body.length > 50 ? '...' : ''}`,
              senderName: lastMessage.senderName,
              eventTitle: conv.eventTitle,
              timestamp: lastMessage.sentAt,
              read: false
            };
            
            setNotifications(prev => {
              if (prev.some(n => n.id === newNotification.id)) return prev;
              return [newNotification, ...prev].slice(0, 10);
            });
            setUnreadCount(prev => prev + 1);
          }
          
          lastCheckRef.current[conv.id] = lastMessage.sentAt;
        }
      }
    } catch (error) {
      console.error('Failed to check messages:', error);
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Initialize last check times on mount
  const initializeLastCheck = useCallback(async () => {
    if (!isAuthenticated()) return;

    try {
      const headers = getAuthHeaders();
      
      // Get current user ID
      const meResponse = await axios.get(`${API}/auth/me`, { headers });
      currentUserIdRef.current = meResponse.data.id;

      const response = await axios.get(`${API}/conversations`, { headers });
      const conversations = response.data;

      for (const conv of conversations) {
        const messagesRes = await axios.get(`${API}/conversations/${conv.id}/messages`, { headers });
        const messages = messagesRes.data;
        
        if (messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          lastCheckRef.current[conv.id] = lastMessage.sentAt;
        }
      }
      
      isInitializedRef.current = true;
      console.log('Notification system initialized');
    } catch (error) {
      console.error('Failed to initialize last check:', error);
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Start polling when component mounts and user is authenticated
  useEffect(() => {
    let isMounted = true;
    let initCheckInterval = null;
    
    const startPolling = async () => {
      if (isAuthenticated() && isMounted && !isInitializedRef.current) {
        // Initialize last check times FIRST
        await initializeLastCheck();
        
        if (isMounted && isInitializedRef.current) {
          // Only start polling AFTER initialization
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = setInterval(checkForNewMessages, 10000);
        }
      }
    };

    // Try to start immediately
    startPolling();
    
    // Also check periodically for token (handles login transition)
    initCheckInterval = setInterval(() => {
      if (isAuthenticated() && !isInitializedRef.current) {
        startPolling();
      }
    }, 2000);

    // Listen for storage changes (login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          startPolling();
        } else {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
          }
          setNotifications([]);
          setUnreadCount(0);
          currentUserIdRef.current = null;
          lastCheckRef.current = {};
          isInitializedRef.current = false;
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      isMounted = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (initCheckInterval) {
        clearInterval(initCheckInterval);
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, checkForNewMessages, initializeLastCheck]);

  // Dismiss a notification
  const dismissNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      dismissNotification,
      clearAllNotifications,
      markAllAsRead,
      checkForNewMessages
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
